import * as React from "react";
import { cn } from "../utils/cn";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "./card";
import { Separator } from "./separator";

export interface ListCardProps extends React.HTMLAttributes<HTMLDivElement> {
    title?: string;
    icon?: React.ReactNode;
    description?: React.ReactNode;
    action?: React.ReactNode;
}

const ListCard = React.forwardRef<HTMLDivElement, ListCardProps>(
    ({ className, title, icon, description, action, children, ...props }, ref) => (
        <Card ref={ref} className={cn("w-full max-w-none", className)} {...props}>
            {(title || icon || description || action) && (
                <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
                    <div className="flex flex-col gap-1">
                        {(title || icon) && (
                            <CardTitle>
                                {icon ? (
                                    <span className="flex items-center gap-2">
                                        {icon}
                                        {title}
                                    </span>
                                ) : (
                                    title
                                )}
                            </CardTitle>
                        )}
                        {description && <CardDescription>{description}</CardDescription>}
                    </div>
                    {action && <div className="shrink-0">{action}</div>}
                </CardHeader>
            )}
            {React.Children.count(children) > 0 && (
                <CardContent className="px-0 pt-0 pb-0">
                    {(title || icon) && <Separator />}
                    <ul className="divide-border divide-y">{children}</ul>
                </CardContent>
            )}
        </Card>
    ),
);
ListCard.displayName = "ListCard";

/* ------------------------------ ListCardRow ------------------------------- */

export interface ListCardRowProps extends React.HTMLAttributes<HTMLLIElement> {
    hideDivider?: boolean;
}

const ListCardRow = React.forwardRef<HTMLLIElement, ListCardRowProps>(
    ({ className, hideDivider, children, ...props }, ref) => (
        <li
            ref={ref}
            className={cn("flex items-center gap-3 px-5 py-3", hideDivider && "!border-t-0", className)}
            {...props}
        >
            {children}
        </li>
    ),
);
ListCardRow.displayName = "ListCardRow";

export { ListCard, ListCardRow };
